import type { Session } from "@supabase/supabase-js";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { clearAccountLibraryCache } from "../saved-library/cache";

export type AccountProfile = {
  id: string;
  username: string;
  displayName: string | null;
  showReportUsername: boolean;
  points: number;
  role: "user" | "admin";
};

type AuthContextValue = {
  loading: boolean;
  session: Session | null;
  profile: AccountProfile | null;
  profileError: string | null;
  needsProfile: boolean;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);

  const loadProfile = useCallback(async (nextSession: Session | null) => {
    setSession(nextSession);
    setProfileError(null);

    if (!nextSession) {
      setProfile(null);
      return;
    }

    const client = await getAuthClient();
    if (!client) {
      setProfile(null);
      setProfileError("Supabase authentication is not configured.");
      return;
    }

    const { data, error } = await client
      .from("users")
      .select("id,username,display_name,show_report_username,points,role")
      .eq("id", nextSession.user.id)
      .maybeSingle();

    if (error) {
      setProfile(null);
      setProfileError("Your Dahlini profile could not be loaded.");
      return;
    }

    setProfile(
      data
        ? {
            id: data.id,
            username: data.username,
            displayName: data.display_name,
            showReportUsername: data.show_report_username,
            points: data.points,
            role: data.role
          }
        : null
    );
  }, []);

  const refreshProfile = useCallback(async () => {
    const client = await getAuthClient();
    if (!client) {
      return;
    }

    const { data } = await client.auth.getSession();
    await loadProfile(data.session);
  }, [loadProfile]);

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | null = null;

    void getAuthClient()
      .then(async (client) => {
        if (!active) {
          return;
        }

        if (!client) {
          setLoading(false);
          return;
        }

        const { data: subscription } = client.auth.onAuthStateChange((_event, nextSession) => {
          if (active) {
            void loadProfile(nextSession).finally(() => {
              if (active) {
                setLoading(false);
              }
            });
          }
        });
        unsubscribe = () => subscription.subscription.unsubscribe();

        const { data } = await client.auth.getSession();
        if (active) {
          await loadProfile(data.session);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setProfileError("Supabase authentication could not be initialized.");
          setLoading(false);
        }
      });

    return () => {
      active = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [loadProfile]);

  const signOut = useCallback(async () => {
    const client = await getAuthClient();
    if (client) {
      await client.auth.signOut();
    }
    if (session) {
      clearAccountLibraryCache(session.user.id);
    }
    setSession(null);
    setProfile(null);
  }, [session]);

  const value = useMemo<AuthContextValue>(
    () => ({
      loading,
      session,
      profile,
      profileError,
      needsProfile: Boolean(session && !profile && !profileError),
      refreshProfile,
      signOut
    }),
    [loading, profile, profileError, refreshProfile, session, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider.");
  }

  return context;
}

async function getAuthClient() {
  const { getSupabaseClient } = await import("../supabase/client");
  return getSupabaseClient();
}
